<template>
  <b-table
    :data="shopsList"
    :striped="true"
    :loading="loading"
    :paginated="showPagination"
    :per-page="rowsPerPage"
    default-sort="shopName"
    default-sort-direction="asc"
    pagination-position="both"
    height="65vh"
    focusable
    scrollable
    sticky-header
  >
    <b-table-column
      v-slot="props"
      sortable
      field="shopId"
      :label="$t('ShopId')"
      centered
    >
      {{ props.row.shopId }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="shopName"
      :label="$t('Shop')"
    >
      {{ props.row.shopName }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="chainName"
      :label="$t('Chain')"
    >
      {{ props.row.chainName }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="shoppingCenterGroupName"
      :label="$t('ShoppingCenterGroup')"
    >
      {{ props.row.shoppingCenterGroupName }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="shoppingCenterName"
      :label="$t('ShoppingCenter')"
    >
      {{ props.row.shoppingCenterName }}
    </b-table-column>
    <b-table-column
      v-slot="props"
      sortable
      field="shoppingCenterShopId"
      :label="$t('ContractId')"
      centered
    >
      {{ props.row.shoppingCenterShopId }}
    </b-table-column>
    <b-table-column
      v-slot="props"
      sortable
      field="chainShopId"
      :label="$t('ChainShopId')"
      centered
    >
      {{ props.row.chainShopId }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="sections"
      :label="$t('Sections')"
    >
      {{ formatSectionNames(props.row.sections) }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="creationDate"
      :label="$t('ShopCreationDate')"
    >
      {{ props.row.creationDate }}
    </b-table-column>
    <b-table-column
      header-class="has-text-right"
      v-slot="props"
      sortable
      field="deactivationDate"
      :label="$t('ShopDeactivationDate')"
    >
      {{ props.row.deactivationDate }}
    </b-table-column>
    <b-table-column
      v-slot="props"
      sortable
      field="membersRelated"
      :label="$t('ShopRelatedMembers')"
      :visible="showRelatedItems"
      centered
    >
      <b-button
        type="is-text"
        @click="showRelatedMembers(props.row.shopId)"
        data-test-id="shopMembersCount"
      >
        {{ props.row.membersRelated?.length || 0 }}
      </b-button>
    </b-table-column>
    <b-table-column
      v-slot="props"
      sortable
      field="groupsRelated"
      :label="$t('ShopRelatedGroups')"
      :visible="showRelatedItems"
      centered
    >
      <b-button
        type="is-text"
        @click="showRelatedGroups(props.row.shopId)"
        data-test-id="shopGroupsCount"
      >
        {{ props.row.groupsRelated?.length || 0 }}
      </b-button>
    </b-table-column>
    <b-table-column
      v-slot="props"
      :label="$t('Edit')"
      centered
    >
      <b-button
        type="is-warning"
        icon-pack="fas"
        icon-right="pencil-alt"
        @click="navigateToEditShop(props.row)"
        data-test-id="shopEditButton"
        :disabled="!props.row.canEditShop"
      />
    </b-table-column>
    <template #top-left>
      <b-field :label="$t('ShopsPerPage')" type="is-blue">
        <b-select v-model="rowsPerPage" expanded @input="setPageSize">
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="75">75</option>
          <option value="100">100</option>
        </b-select>
      </b-field>
    </template>
    <template #empty>
      <div v-if="loading" class="content has-text-grey has-text-centered">
        <p>{{ $t("Loading") }}</p>
      </div>
      <div v-else class="content has-text-grey has-text-centered">
        <p><b-icon pack="fas" icon="frown-open" size="is-large" /></p>
        <p>{{ $t("NoDataToShow") }}</p>
      </div>
    </template>
  </b-table>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { type Shop } from '@/stores/app'
import localStorageAccess from '@/resourceAccess/localStorageAccess'

interface Props {
  shopsList: Shop[]
  loading: boolean
  showRelatedItems: boolean
}

type ShopSection = NonNullable<Shop['sections']>[number]

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  showRelatedItems: true,
})

const emit = defineEmits<{
  (e: 'member-count-clicked', shopId: number): void
  (e: 'notification-group-count-clicked', shopId: number): void
}>()

const router = useRouter()
const route = useRoute()
const rowsPerPage = ref<number>(10)

const showPagination = computed(() => {
  return props.shopsList ? props.shopsList.length > 10 : false
})

onMounted(() => {
  const savedRowsPerPage = localStorageAccess.getRowsPerPage('shopsList')
  if (savedRowsPerPage) {
    rowsPerPage.value = parseInt(savedRowsPerPage, 10) || 10
  }
})

function setPageSize() {
  localStorageAccess.saveRowsPerPage('shopsList', rowsPerPage.value)
}

function showRelatedMembers(shopId: number) {
  emit('member-count-clicked', shopId)
}

function showRelatedGroups(shopId: number) {
  emit('notification-group-count-clicked', shopId)
}

function navigateToEditShop(shop: Shop) {
  router.push({
    name: 'shopAdminEdit',
    params: { id: shop.shopId.toString() },
    query: { returnTo: route.path },
  })
}

function formatSectionNames(sections: Shop['sections']): string {
  return (sections ?? []).map((section: ShopSection) => section.sectionName).join(', ')
}
</script>
